import { motion, AnimatePresence } from "framer-motion";
import { AirVent, Camera, Headphones, Laptop, Package, ShoppingBag, Smartphone, Tablet, Tv, Watch } from "lucide-react";

// ─── PRODUCT CONFIG — each item gets icon + accent color ──────────────────────
const PRODUCT_CONFIG = {
  "Mobile Phones": {
    icon: Smartphone,
    color: "#6C63FF",
    bg: "#F0EFFF",
  },
  "Laptops": {
    icon: Laptop,
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  "Tablets": {
    icon: Tablet,
    color: "#0891B2",
    bg: "#ECFEFF",
  },
  "Smart Watches": {
    icon: Watch,
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  "Headphones": {
    icon: Headphones,
    color: "#DB2777",
    bg: "#FDF2F8",
  },
  "Cameras": {
    icon: Camera,
    color: "#D97706",
    bg: "#FFFBEB",
  },
  "TVs": {
    icon: Tv,
    color: "#059669",
    bg: "#ECFDF5",
  },
  "Refrigerators": {
    icon: Package, // fallback
    color: "#0369A1",
    bg: "#F0F9FF",
  },
  "Washing Machines": {
    icon: Package, // fallback
    color: "#0D9488",
    bg: "#F0FDFA",
  },
  "Air Conditioners": {
    icon: AirVent,
    color: "#1D4ED8",
    bg: "#EFF6FF",
  },
  "Accessories": {
    icon: ShoppingBag,
    color: "#92400E",
    bg: "#FFFBEB",
  },
  "Other": {
    icon: Package,
    color: "#64748B",
    bg: "#F8FAFC",
  },
};

function getConfig(item) {
  return PRODUCT_CONFIG[item] || { icon: "🛍️", color: "#6366F1", bg: "#EEF2FF" };
}

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: ProductChip
// ─────────────────────────────────────────────────────────────────────────────
function ProductChip({ item, isSelected, onToggle, index }) {
  const cfg = getConfig(item);
  const Icon = cfg.icon;


  return (
    <motion.button
      type="button"
      onClick={() => onToggle(item)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0  }}
      transition={{ duration: 0.25, delay: index * 0.03 }}
      whileTap={{ scale: 0.94 }}
      className="relative flex items-center gap-2 px-2 py-2.5 rounded-2xl
        text-[12px] font-semibold transition-all duration-200 select-none"
      style={{
        background: isSelected ? "#6C63FF" : "#F1F5F9",
        color: isSelected ? "#ffffff" : "#334155",
        border: isSelected ? "2px solid #6C63FF" : "2px solid #E2E8F0",
        boxShadow: isSelected
        ? "0 6px 18px rgba(108,99,255,0.35)"
        : "none",
        
        transform:   isSelected ? "translateY(-2px)"            : "translateY(0)",
      }}
    >
      {/* Icon */}
      <Icon className="w-3 h-3" />

      {/* Label */}
      <span className="leading-none">{item}</span>

      {/* Checkmark tick — animates in when selected */}
      <AnimatePresence>
        {isSelected && (
          <motion.span
            key="check"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1   }}
            exit={{   opacity: 0, scale: 0.5  }}
            transition={{ duration: 0.15 }}
            className="ml-0.5 flex items-center justify-center w-3 h-3 rounded-full bg-white/30"
            style={{ fontSize: 10 }}
          >
            ✓
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: ProductCatalogSelector  — drop-in replacement
// ─────────────────────────────────────────────────────────────────────────────
export function ProductCatalogSelector({
  productOptions = [],
  selectedCatalog = [],
  toggleCatalog,
}) {
  const count = selectedCatalog.length;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2
            className="font-display font-bold leading-tight"
            style={{ fontSize: 17, color: "var(--color-text-primary, #1E1B4B)" }}
          >
            What products do you sell?
          </h2>
          <p
            className="font-body mt-0.5"
            style={{ fontSize: 12, color: "var(--color-text-muted, #9CA3AF)" }}
          >
            Select all that apply
          </p>
          {count === 0 && (
            <p className="text-sm text-red-500 font-medium">
                Please select at least one product
            </p>
            )}
        </div>

        {/* Selection counter badge */}
        <AnimatePresence>
          {count > 0 && (
            <motion.div
              key="counter"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1   }}
              exit={{   opacity: 0, scale: 0.7  }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-[12px] font-bold"
              style={{
                background:  "var(--color-brand, #6C63FF)",
                boxShadow:   "0 4px 12px rgba(108,99,255,0.35)",
              }}
            >
              <span className="text-[10px]">✓</span>
              {count} selected
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Chip grid */}
      <div className="flex flex-wrap gap-2.5">
        {productOptions.map((item, i) => (
          <ProductChip
            key={item}
            item={item}
            index={i}
            isSelected={selectedCatalog.includes(item)}
            onToggle={toggleCatalog}
          />
        ))}
      </div>

      {/* Selected summary */}
      <AnimatePresence>
        {count > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{   opacity: 0, height: 0      }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              className="flex flex-wrap items-center gap-1.5 px-4 py-3 rounded-2xl"
              style={{
                background: "var(--color-brand-surface, #F0EFFF)",
                border:     "1px solid var(--color-brand-border, #EDE9FE)",
              }}
            >
              <span
                className="text-[11px] font-bold uppercase tracking-widest mr-1"
                style={{ color: "var(--color-brand, #6C63FF)" }}
              >
                Selling:
              </span>
              {selectedCatalog.map(item => {
                    const cfg = getConfig(item);
                    const Icon = cfg.icon;

                    return (
                        <span
                        key={item}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{ background: cfg.bg, color: cfg.color }}
                        >
                        <Icon className="w-3 h-3" />
                        {item}
                        </span>
                    );
                    })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}